'use client';
import React, { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    title: 'Create your profile',
    desc: 'Sellers build a verified profile with TikTok Shop stats, niche, past GMV, and availability. Brands set up their company profile and campaign goals.',
    color: 'bg-theme-02',
    textColor: 'text-dark',
  },
  {
    number: '02',
    title: 'Get matched instantly',
    desc: 'Our AI engine surfaces the best matches based on niche alignment, audience fit, budget, and performance history. Browse or let us recommend.',
    color: 'bg-theme-01',
    textColor: 'text-white',
  },
  {
    number: '03',
    title: 'Connect & collaborate',
    desc: 'Message directly, review proposals, negotiate terms, and sign contracts — all within the Talent platform. No back-and-forth emails.',
    color: 'bg-theme-04',
    textColor: 'text-dark',
  },
  {
    number: '04',
    title: 'Go live & get paid',
    desc: 'Sellers go live, brands track performance in real time, and payments are released automatically via our secure escrow system.',
    color: 'bg-theme-03',
    textColor: 'text-dark',
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const items = entry.target.querySelectorAll('.step-item');
            items.forEach((item, i) => {
              setTimeout(() => {
                (item as HTMLElement).style.opacity = '1';
                (item as HTMLElement).style.transform = 'translateY(0)';
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-16">
          <div className="mb-5">
            <div
              className="bg-theme-01 text-white text-[14px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
              style={{ transform: 'rotate(-6deg)' }}
            >
              How it works
            </div>
          </div>
          <h2 className="text-[40px] md:text-[48px] font-bold text-dark leading-tight mb-4">
            From sign-up to live stream in minutes
          </h2>
          <p className="text-[18px] text-dark/60 max-w-[560px] mx-auto leading-relaxed">
            Talent makes it effortless for sellers and brands to find each other and start collaborating.
          </p>
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((step, i) => (
            <div
              key={i}
              className={`step-item ${step.color} rounded-[24px] p-7 relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg`}
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.5s ease, transform 0.5s ease' }}
            >
              <div className={`text-[48px] font-bold ${step.textColor} opacity-20 leading-none mb-4 font-montserrat`}>
                {step.number}
              </div>
              <h3 className={`text-[18px] font-bold ${step.textColor} mb-3`}>{step.title}</h3>
              <p className={`text-[14px] ${step.textColor} opacity-70 leading-relaxed`}>{step.desc}</p>

              {/* Connector arrow (not on last) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-10">
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14M12 5l7 7-7 7" stroke="#120A0B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Stats bar */}
        <div className="mt-14 bg-dark rounded-[24px] p-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: '12,400+', label: 'Active Sellers' },
            { value: '3,200+', label: 'Brands Hiring' },
            { value: '$2.4B+', label: 'GMV Generated' },
            { value: '48 hrs', label: 'Avg. Time to Match' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <p className="text-[32px] md:text-[36px] font-bold text-white mb-1">{stat.value}</p>
              <p className="text-[14px] text-white/50 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
